import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { BookingStepper } from '../components/quote-v2';
import {
  OrderSummary,
  PaymentMethodCard,
  PaymentPriceSummary,
  MobilePaymentBar,
} from '../components/payment';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { getAutoIncludedExtras } from '../config/extrasConfig';
import useClarityPageReady from '../hooks/useClarityPageReady';
import { AnalyticsEvents } from '../utils/analytics';
import { ArrowLeft, Tag, Check, AlertCircle, Info, Banknote, Loader2, CreditCard, Shield } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = window.location.origin || '';

// Format date from raw strings
const formatDateForDisplay = (dateStr, timeStr) => {
  if (!dateStr) return 'Date not set';
  try {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const weekday = date.toLocaleDateString('en-GB', { weekday: 'short' });
    const monthName = date.toLocaleDateString('en-GB', { month: 'short' });
    return `${weekday}, ${day} ${monthName} ${year} at ${timeStr || '00:00'}`;
  } catch (e) {
    return `${dateStr} ${timeStr || ''}`;
  }
};

const PaymentPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Clarity page ready signal
  useClarityPageReady('payment');

  // Session state
  const [sessionData, setSessionData] = useState(null);
  const [isSessionLoading, setIsSessionLoading] = useState(true);

  // Get session ID from URL - support both 'sid' and 'session_id' params
  const sessionId = searchParams.get('sid') || searchParams.get('session_id');

  // Fetch session data
  const fetchSession = useCallback(async () => {
    if (!sessionId) {
      setIsSessionLoading(false);
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/booking/${sessionId}`);
      const result = await res.json();
      
      if (result.success && result.session) {
        setSessionData(result.session);
        console.log('[PaymentPage] Session loaded:', {
          vehicle_price: result.session.vehicle_price,
          subtotal: result.session.subtotal,
          total: result.session.total
        });
      }
    } catch (error) {
      console.error('Failed to fetch session:', error);
    } finally {
      setIsSessionLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  // Payment methods from API
  const [availableMethods, setAvailableMethods] = useState([]);
  const [loadingMethods, setLoadingMethods] = useState(true);

  // Payment method state - will be set to first available after API load
  const [paymentMethod, setPaymentMethod] = useState('');

  // Coupon state (preserved from Details page or new)
  const [couponCode, setCouponCode] = useState(searchParams.get('couponCode') || '');
  const [couponDiscount, setCouponDiscount] = useState(
    parseInt(searchParams.get('couponDiscount')) || 0
  );
  const [couponApplied, setCouponApplied] = useState(!!searchParams.get('couponCode'));
  const [couponError, setCouponError] = useState('');

  // Legal agreements state
  const [agreements, setAgreements] = useState({
    terms: false,
    privacy: false,
    refund: false,
  });

  // Processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  
  // PayPal state
  const [paypalClientId, setPaypalClientId] = useState(null);
  const [paypalMode, setPaypalMode] = useState('sandbox');

  // SECURITY: Quote state for server-side pricing
  const [quoteId, setQuoteId] = useState(null);
  const [quoteData, setQuoteData] = useState(null);
  const [isCreatingQuote, setIsCreatingQuote] = useState(false);
  const [quoteError, setQuoteError] = useState('');

  // Check for cancelled payment
  useEffect(() => {
    if (searchParams.get('cancelled') === 'true') {
      setPaymentError('Payment was cancelled. Please try again.');
    }
  }, [searchParams]);

  // SECURITY: Create server-side quote when session is loaded
  const createQuote = useCallback(async () => {
    if (!sessionId || !sessionData || isCreatingQuote || quoteId) return;
    
    // Don't create quote if no vehicle selected
    if (!sessionData.vehicle_id && !sessionData.zuppin_vehicle_id) {
      console.log('[PaymentPage] No vehicle selected, skipping quote creation');
      return;
    }
    
    setIsCreatingQuote(true);
    setQuoteError('');
    
    try {
      const response = await fetch(`${API_BASE}/api/quote/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sessionId,
          vehicleId: sessionData.vehicle_id || sessionData.zuppin_vehicle_id,
          tripOptionsId: sessionData.zuppin_trip_options_id,
          extras: sessionData.extras || []
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setQuoteId(data.quoteId);
        setQuoteData({
          totalPrice: data.totalPrice,
          currency: data.currency,
          expiresAt: data.expiresAt,
          breakdown: data.breakdown
        });
        console.log('[PaymentPage] Quote created:', data.quoteId, 'Total:', data.totalPrice);
      } else {
        console.error('[PaymentPage] Quote creation failed:', data);
        // Handle FastAPI validation errors (detail is array) or string messages
        let errorMsg = 'Failed to create quote. Please refresh and try again.';
        if (data.detail) {
          if (Array.isArray(data.detail)) {
            errorMsg = data.detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
          } else if (typeof data.detail === 'string') {
            errorMsg = data.detail;
          }
        }
        setQuoteError(errorMsg);
      }
    } catch (error) {
      console.error('[PaymentPage] Quote creation error:', error);
      setQuoteError('Failed to create secure quote. Please refresh the page.');
    } finally {
      setIsCreatingQuote(false);
    }
  }, [sessionId, sessionData, isCreatingQuote, quoteId]);

  // Create quote when session loads
  useEffect(() => {
    if (sessionData && !quoteId && !isCreatingQuote) {
      createQuote();
    }
  }, [sessionData, quoteId, isCreatingQuote, createQuote]);

  // Get trip details from session or URL params
  const tripDetails = useMemo(() => {
    if (sessionData) {
      const date = sessionData.trip_date || '';
      const time = sessionData.trip_time || '';
      return {
        pickup: sessionData.pickup_location || 'Istanbul Airport',
        dropoff: sessionData.dropoff_location || '',
        date: date,
        time: time,
        formattedDate: formatDateForDisplay(date, time),
        passengers: sessionData.passengers || 1,
        luggage: sessionData.luggage || 1,
        timezone: sessionData.timezone || 'Europe/Istanbul',
        pickupType: sessionData.pickup_type || 'manual',
      };
    }
    return {
      pickup: searchParams.get('pickup') || 'Istanbul Airport (IST)',
      dropoff: searchParams.get('dropoff') || 'Taksim Square',
      date: searchParams.get('date') || '',
      time: searchParams.get('time') || '',
      formattedDate: '',
      passengers: parseInt(searchParams.get('passengers')) || 1,
      luggage: parseInt(searchParams.get('luggage')) || 1,
      pickupType: 'manual',
    };
  }, [sessionData, searchParams]);

  // Get vehicle details from session or URL params
  const vehicleDetails = useMemo(() => {
    // While loading, return null to show loading state
    if (isSessionLoading) {
      return null;
    }
    
    if (sessionData) {
      // Check for vehicle data in session (saved by Quote page)
      const vehicleSnapshot = sessionData.selected_vehicle_snapshot || sessionData.selected_vehicle;
      
      // Priority: session.vehicle_price > session.subtotal > snapshot price > URL param
      let vehiclePrice = sessionData.vehicle_price;
      if (!vehiclePrice && vehiclePrice !== 0) {
        vehiclePrice = sessionData.subtotal;
      }
      if (!vehiclePrice && vehiclePrice !== 0 && vehicleSnapshot) {
        vehiclePrice = vehicleSnapshot.price;
      }
      
      // Ensure it's a number
      vehiclePrice = parseFloat(vehiclePrice) || 0;
      
      if (vehicleSnapshot || vehiclePrice > 0) {
        return {
          id: vehicleSnapshot?.type || vehicleSnapshot?.vehicleType || vehicleSnapshot?.id || 'VAN',
          name: sessionData.vehicle_name || vehicleSnapshot?.name || vehicleSnapshot?.categoryDisplayName || 'VIP Van',
          price: vehiclePrice,
        };
      }
    }
    
    // Fallback to URL params only if session loaded but has no data
    const priceFromUrl = parseFloat(searchParams.get('price')) || 0;
    return {
      id: searchParams.get('vehicleType') || 'VAN',
      name: searchParams.get('vehicle') || 'VIP Van',
      price: priceFromUrl,
    };
  }, [sessionData, searchParams, isSessionLoading]);

  // Get extras total from URL params OR session data (session is authoritative)
  const extrasTotal = useMemo(() => {
    // Prefer session data as it's the authoritative source from backend
    if (sessionData?.extras_total !== undefined) {
      return parseFloat(sessionData.extras_total) || 0;
    }
    // Fallback to URL param
    return parseInt(searchParams.get('extrasTotal')) || 0;
  }, [sessionData, searchParams]);

  // Fire begin_checkout event when payment page loads with valid data
  useEffect(() => {
    if (vehicleDetails?.price && sessionData) {
      AnalyticsEvents.beginCheckout({
        vehicleType: vehicleDetails.type || vehicleDetails.name,
        vehicleName: vehicleDetails.name,
        totalPrice: vehicleDetails.price,
        route: `${tripDetails.pickup} -> ${tripDetails.dropoff}`,
      });
    }
  }, [vehicleDetails, sessionData, tripDetails.pickup, tripDetails.dropoff]);

  // Parse extras from session data OR URL params (session is authoritative)
  const selectedExtras = useMemo(() => {
    // Prefer session data as it's the authoritative source from backend
    if (sessionData?.extras && Array.isArray(sessionData.extras)) {
      return sessionData.extras;
    }
    // Fallback to URL param
    try {
      const extrasParam = searchParams.get('extras');
      return extrasParam ? JSON.parse(extrasParam) : [];
    } catch (e) {
      return [];
    }
  }, [sessionData, searchParams]);

  // Customer details from URL
  const customerDetails = useMemo(() => ({
    email: searchParams.get('email') || '',
    name: searchParams.get('name') || '',
    phone: searchParams.get('phone') || '',
  }), [searchParams]);

  // SECURITY: Use server-side quote price when available
  const totalPrice = useMemo(() => {
    // If we have a quote, use the server-side price (AUTHORITATIVE)
    if (quoteData?.totalPrice) {
      return quoteData.totalPrice;
    }
    // Fallback to calculated price (display only before quote created)
    const vehiclePrice = vehicleDetails?.price || 0;
    const raw = vehiclePrice + extrasTotal - couponDiscount;
    return Math.round(raw * 100) / 100;
  }, [quoteData?.totalPrice, vehicleDetails?.price, extrasTotal, couponDiscount]);

  // Auto-included extras (gifts) - from extrasConfig
  const autoIncludedExtras = useMemo(() => {
    return getAutoIncludedExtras();
  }, []);
  
  const allAgreed = agreements.terms && agreements.privacy && agreements.refund;

  // Fetch available payment methods from API
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/payment/methods`);
        const data = await response.json();
        
        if (data.success && data.methods) {
          setAvailableMethods(data.methods);
          // Set default to first available method
          if (data.methods.length > 0 && !paymentMethod) {
            // Prefer PayPal if available, then Cash, then Card
            const paypal = data.methods.find(m => m.id === 'paypal');
            const cash = data.methods.find(m => m.id === 'cash');
            const card = data.methods.find(m => m.id === 'card');
            if (paypal) {
              setPaymentMethod('paypal');
            } else if (card) {
              setPaymentMethod('card');
            } else if (cash) {
              setPaymentMethod('cash');
            } else {
              setPaymentMethod(data.methods[0].id);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch payment methods:', error);
        // Fallback to default methods
        setAvailableMethods([
          { id: 'paypal', name: 'PayPal', description: 'Pay securely with PayPal', enabled: true },
          { id: 'card', name: 'Credit / Debit Card', description: 'Pay with Visa, Mastercard', enabled: true },
          { id: 'cash', name: 'Cash Payment', description: 'Pay to driver upon arrival', enabled: true }
        ]);
        setPaymentMethod('paypal');
      } finally {
        setLoadingMethods(false);
      }
    };
    
    fetchPaymentMethods();
  }, []);

  // Fetch PayPal client ID for SDK
  // Fire add_payment_info when payment method changes
  useEffect(() => {
    if (paymentMethod && vehicleDetails?.price) {
      AnalyticsEvents.addPaymentInfo({
        totalPrice: vehicleDetails.price,
        paymentMethod: paymentMethod,
        vehicleType: vehicleDetails.type || vehicleDetails.name,
        vehicleName: vehicleDetails.name,
      });
    }
  }, [paymentMethod, vehicleDetails]);

  useEffect(() => {
    const fetchPayPalSettings = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/payments/paypal/client-id`);
        const data = await response.json();
        
        if (data.success && data.clientId) {
          setPaypalClientId(data.clientId);
          setPaypalMode(data.mode || 'sandbox');
        }
      } catch (error) {
        console.error('Failed to fetch PayPal settings:', error);
      }
    };
    
    fetchPayPalSettings();
  }, []);

  // Handle coupon apply - using real API
  const handleApplyCoupon = async () => {
    setCouponError('');
    
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }
    
    // Don't allow coupon application while loading
    if (!vehicleDetails) {
      setCouponError('Please wait for booking details to load');
      return;
    }

    try {
      // Build context for coupon validation
      const pickupDatetime = `${tripDetails.date}T${tripDetails.time}:00Z`;
      
      // Extract airport code from pickup location
      let airportCode = '';
      const airportMatch = tripDetails.pickup.match(/\(([A-Z]{3})\)/);
      if (airportMatch) {
        airportCode = airportMatch[1];
      }
      
      const response = await fetch(`${API_BASE}/api/coupons/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: couponCode.toUpperCase(),
          session_id: sessionId,  // Include session ID to update with discount
          context: {
            price: (vehicleDetails?.price || 0) + extrasTotal,
            currency: 'EUR',
            airport: airportCode,
            vehicle_type: vehicleDetails?.id || 'VAN',
            booking_type: 'transfer',
            pickup_datetime: pickupDatetime,
            passengers: tripDetails.passengers,
            customer_email: customerDetails.email || null,
          }
        })
      });
      
      const data = await response.json();
      
      if (data.valid) {
        setCouponDiscount(data.discount_amount);
        setCouponApplied(true);
        toast.success(`Coupon applied! €${data.discount_amount.toFixed(2)} discount`);
        
        // CRITICAL: Update session with coupon info so it's saved in booking
        if (sessionId) {
          try {
            await fetch(`${API_BASE}/api/booking/coupon`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                sessionId: sessionId,
                code: couponCode.toUpperCase()
              })
            });
          } catch (e) {
            console.error('Failed to update session with coupon:', e);
          }
        }
      } else {
        // Map reason codes to user-friendly messages
        const reasonMessages = {
          'INVALID_CODE': 'Invalid or expired coupon code',
          'EXPIRED': 'This coupon has expired',
          'SCHEDULED': 'This coupon is not yet active',
          'DISABLED': 'This coupon is no longer available',
          'LIMIT_REACHED': 'This coupon has reached its usage limit',
          'MIN_ORDER_NOT_MET': 'Minimum order amount not met for this coupon',
          'NOT_ELIGIBLE': 'This coupon is not valid for your order',
          'CURRENCY_MISMATCH': 'This coupon is not valid for your currency',
        };
        setCouponError(reasonMessages[data.reason] || 'Invalid coupon code');
        setCouponApplied(false);
      }
    } catch (error) {
      console.error('Coupon apply error:', error);
      setCouponError('Failed to apply coupon. Please try again.');
      setCouponApplied(false);
    }
  };

  // Handle payment
  const handlePay = async () => {
    // Reset error
    setPaymentError('');

    // Check agreements
    if (!allAgreed) {
      toast.error('Please accept all required agreements', {
        style: { background: '#FF8C00', color: 'white', border: 'none' }
      });
      return;
    }

    // SECURITY: Require quote for card and paypal payments
    if ((paymentMethod === 'card' || paymentMethod === 'paypal') && !quoteId) {
      if (isCreatingQuote) {
        toast.info('Please wait, creating secure quote...');
        return;
      }
      // Try to create quote
      await createQuote();
      if (!quoteId) {
        toast.error('Unable to create secure quote. Please refresh and try again.');
        return;
      }
    }

    // Start processing - prevent double click
    setIsProcessing(true);

    try {
      if (paymentMethod === 'card') {
        // SECURITY: Use secure Stripe checkout with quoteId (NO AMOUNT from client)
        const response = await fetch(`${API_BASE}/api/secure/stripe/checkout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            quoteId: quoteId, // Server will get price from quote
            sessionId: sessionId,
            customerEmail: customerDetails.email,
            customerName: customerDetails.name
          })
        });

        const data = await response.json();

        if (data.success && data.checkoutUrl) {
          // Redirect to Stripe Checkout
          window.location.href = data.checkoutUrl;
          return;
        } else {
          throw new Error(data.detail || data.error || data.message || 'Failed to create payment session');
        }
      } else if (paymentMethod === 'paypal') {
        // SECURITY: Use secure PayPal order with quoteId
        const response = await fetch(`${API_BASE}/api/secure/paypal/create-order`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            quoteId: quoteId,
            sessionId: sessionId
          })
        });

        const data = await response.json();

        if (data.success && data.approvalUrl) {
          // Redirect to PayPal approval
          window.location.href = data.approvalUrl;
          return;
        } else {
          throw new Error(data.detail || data.error || data.message || 'PayPal payment failed');
        }
      } else if (paymentMethod === 'cash') {
        // Cash payment - use secure booking with quoteId
        const response = await fetch(`${API_BASE}/api/booking/secure/create`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            quoteId: quoteId || sessionData?.active_quote_id,
            sessionId: sessionId,
            paymentMethod: 'cash',
            customerName: customerDetails.name,
            customerEmail: customerDetails.email,
            customerPhone: customerDetails.phone,
            flightNumber: sessionData?.flight_number,
            customerNotes: sessionData?.customer_notes
          })
        });

        const data = await response.json();

        if (data.success) {
          // Navigate to confirmation with booking reference from backend
          navigate(`/confirm?ref=${data.bookingRef}&paymentMethod=cash`);
        } else {
          throw new Error(data.detail || data.error || data.message || 'Failed to create booking');
        }
      }
    } catch (error) {
      console.error('Payment error:', error);
      setPaymentError(
        error.message || 'Something went wrong. Please try again or choose a different payment method.'
      );
      setIsProcessing(false);
    }
  };

  // Handle agreement change
  const handleAgreementChange = (key, value) => {
    setAgreements(prev => ({ ...prev, [key]: value }));
  };

  // Handle back
  const handleBack = () => {
    navigate(-1);
  };

  // Can pay check - simplified for Stripe Checkout (no card form needed)
  const canPay = paymentMethod !== '';

  return (
    <div className="min-h-screen bg-black text-white">
      {/* noindex for booking flow pages */}
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>
      
      <Header />

      <main className="pt-28 md:pt-32 pb-32 lg:pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <div className="mb-4">
            <Button
              variant="outline"
              onClick={handleBack}
              className="text-white border-white/30 hover:bg-white/10 hover:text-white bg-white/5"
              data-testid="back-btn"
              disabled={isProcessing}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Details
            </Button>
          </div>

          {/* Booking Stepper - Step 4 Active */}
          <BookingStepper currentStep={4} />

          {/* Page Title */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-white mb-2">Payment</h1>
            <p className="text-gray-400">Choose your payment method to complete your booking</p>
          </div>

          {/* Payment Error Banner */}
          {paymentError && (
            <div className="bg-[#FF8C00]/10 border border-[#FF8C00] rounded-xl p-4 mb-6 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-[#FF8C00] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white font-medium text-sm">{paymentError}</p>
              </div>
            </div>
          )}

          {/* Quote Error Banner */}
          {quoteError && (
            <div className="bg-red-500/10 border border-red-500 rounded-xl p-4 mb-6 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white font-medium text-sm">{quoteError}</p>
                <button 
                  onClick={createQuote}
                  className="text-red-400 underline text-sm mt-1"
                >
                  Try again
                </button>
              </div>
            </div>
          )}

          {/* Security Badge - Quote Created */}
          {quoteId && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3 mb-6 flex items-center gap-3">
              <Shield className="h-5 w-5 text-green-500 flex-shrink-0" />
              <p className="text-green-400 text-sm">
                Secure quote created. Price locked at €{quoteData?.totalPrice?.toFixed(2) || totalPrice.toFixed(2)}
              </p>
            </div>
          )}

          {/* Main 3-Column Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left: Order Summary */}
            <div className="lg:col-span-3 hidden lg:block">
              <div className="sticky top-28">
                <OrderSummary
                  tripDetails={tripDetails}
                  vehicleDetails={vehicleDetails}
                  selectedExtras={selectedExtras}
                />
              </div>
            </div>

            {/* Center: Payment Methods */}
            <div className="lg:col-span-6 space-y-6">
              {/* Payment Method Selection */}
              <div>
                <h2 className="text-lg font-semibold text-white mb-4">
                  Choose your payment method
                </h2>

                {loadingMethods ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Dynamically render available payment methods */}
                    {availableMethods.map((method) => {
                      if (method.id === 'paypal') {
                        return (
                          <div key={method.id}>
                            <PaymentMethodCard
                              type="paypal"
                              isSelected={paymentMethod === 'paypal'}
                              onSelect={() => setPaymentMethod('paypal')}
                            />
                            
                            {/* PayPal Buttons - show when PayPal is selected and quote exists */}
                            {paymentMethod === 'paypal' && paypalClientId && allAgreed && quoteId && (
                              <div className="mt-4 bg-white rounded-xl p-4">
                                <PayPalScriptProvider options={{
                                  "client-id": paypalClientId,
                                  currency: "EUR",
                                  intent: "capture",
                                }}>
                                  <PayPalButtons
                                    style={{
                                      layout: "vertical",
                                      color: "gold",
                                      shape: "rect",
                                      label: "pay",
                                      height: 45,
                                    }}
                                    disabled={isProcessing || !quoteId}
                                    createOrder={async () => {
                                      try {
                                        setIsProcessing(true);
                                        setPaymentError('');
                                        
                                        // SECURITY: Use secure PayPal endpoint with quoteId (NO AMOUNT)
                                        const response = await fetch(`${API_BASE}/api/secure/paypal/create-order`, {
                                          method: 'POST',
                                          headers: { 'Content-Type': 'application/json' },
                                          body: JSON.stringify({
                                            quoteId: quoteId,
                                            sessionId: sessionId
                                          }),
                                        });
                                        
                                        const data = await response.json();
                                        
                                        if (data.success && data.orderId) {
                                          return data.orderId;
                                        } else {
                                        throw new Error(data.error || data.detail || 'Failed to create PayPal order');
                                        }
                                      } catch (error) {
                                        setPaymentError(error.message);
                                        toast.error(error.message);
                                        throw error;
                                      } finally {
                                        setIsProcessing(false);
                                      }
                                    }}
                                    onApprove={async (data) => {
                                      try {
                                        setIsProcessing(true);
                                        
                                        const response = await fetch(`${API_BASE}/api/payments/paypal/capture-order`, {
                                          method: 'POST',
                                          headers: { 'Content-Type': 'application/json' },
                                          body: JSON.stringify({
                                            order_id: data.orderID,
                                            session_id: sessionId,
                                          }),
                                        });
                                        
                                        const result = await response.json();
                                        
                                        if (result.success && result.bookingRef) {
                                        toast.success('Payment successful! Your booking is confirmed.');
                                          navigate(`/confirm?ref=${result.bookingRef}`);
                                        } else {
                                        throw new Error(result.error || 'Payment could not be completed');
                                        }
                                      } catch (error) {
                                        setPaymentError(error.message);
                                        toast.error(error.message);
                                      } finally {
                                        setIsProcessing(false);
                                      }
                                    }}
                                    onCancel={() => {
                                    toast.info('Payment was cancelled');
                                    }}
                                    onError={(err) => {
                                      console.error('PayPal error:', err);
                                    setPaymentError('PayPal error occurred. Please try again.');
                                      toast.error('PayPal error occurred');
                                    }}
                                  />
                                </PayPalScriptProvider>
                              </div>
                            )}
                            
                            {/* Loading state while creating quote */}
                            {paymentMethod === 'paypal' && paypalClientId && allAgreed && !quoteId && isCreatingQuote && (
                              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                <div className="flex items-center gap-2">
                                  <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                                  <p className="text-sm text-blue-800">
                                    Creating secure quote...
                                  </p>
                                </div>
                              </div>
                            )}
                            
                            {/* Message when not all agreements are checked */}
                            {paymentMethod === 'paypal' && paypalClientId && !allAgreed && (
                              <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                                <div className="flex items-start gap-2">
                                  <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5" />
                                  <p className="text-sm text-yellow-800">
                                    Please accept all terms to pay with PayPal.
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      }
                      
                      if (method.id === 'cash') {
                        return (
                          <div
                            key={method.id}
                            onClick={() => setPaymentMethod('cash')}
                            className={`bg-white rounded-xl p-5 cursor-pointer transition-all ${
                              paymentMethod === 'cash'
                                ? 'ring-2 ring-[#FF8C00]'
                                : 'hover:bg-gray-50'
                            }`}
                            data-testid="cash-payment-option"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className={`p-3 rounded-lg ${
                                  paymentMethod === 'cash' 
                                    ? 'bg-green-100 text-green-600' 
                                    : 'bg-gray-100 text-gray-600'
                                }`}>
                                  <Banknote className="h-6 w-6" />
                                </div>
                                <div>
                                  <h3 className="font-semibold text-black">Cash Payment</h3>
                                  <p className="text-sm text-gray-500">Pay to driver upon arrival</p>
                                </div>
                              </div>
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                paymentMethod === 'cash'
                                  ? 'border-[#FF8C00] bg-[#FF8C00]'
                                  : 'border-gray-300'
                              }`}>
                                {paymentMethod === 'cash' && (
                                  <Check className="h-3 w-3 text-white" />
                                )}
                              </div>
                            </div>
                            
                            {paymentMethod === 'cash' && (
                              <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                                <div className="flex items-start gap-2">
                                  <Info className="h-4 w-4 text-green-600 mt-0.5" />
                                  <p className="text-sm text-green-800">
                                    No payment required now. Pay the full amount in cash (EUR) to your driver when you arrive at your destination.
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      }
                      
                      if (method.id === 'card') {
                        return (
                          <div
                            key={method.id}
                            onClick={() => setPaymentMethod('card')}
                            className={`relative bg-white rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                              paymentMethod === 'card'
                                ? 'border-[#FF8C00] shadow-md'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            data-testid="card-payment-option"
                          >
                            {/* Selected indicator */}
                            {paymentMethod === 'card' && (
                              <div className="absolute -top-2 -right-2 w-6 h-6 bg-[#FF8C00] rounded-full flex items-center justify-center">
                                <Check className="h-4 w-4 text-white" />
                              </div>
                            )}

                            <div className="p-5">
                              {/* Header */}
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                                    <CreditCard className="h-6 w-6 text-gray-600" />
                                  </div>
                                  <div>
                                    <h3 className="font-semibold text-black">Credit / Debit Card</h3>
                                    <p className="text-sm text-gray-500">Secure payment via Stripe</p>
                                  </div>
                                </div>

                                {/* Radio indicator */}
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                                  paymentMethod === 'card' ? 'border-[#FF8C00] bg-[#FF8C00]' : 'border-gray-300'
                                }`}>
                                  {paymentMethod === 'card' && <div className="w-2 h-2 bg-white rounded-full" />}
                                </div>
                              </div>

                              {/* Card logos */}
                              <div className="flex items-center gap-2 mb-3">
                                <div className="w-10 h-6 bg-[#1A1F71] rounded flex items-center justify-center">
                                  <span className="text-white text-[8px] font-bold">VISA</span>
                                </div>
                                <div className="w-10 h-6 bg-[#EB001B] rounded flex items-center justify-center relative overflow-hidden">
                                  <div className="absolute right-0 w-5 h-6 bg-[#F79E1B] rounded-l-full" />
                                </div>
                                <div className="w-10 h-6 bg-gray-100 rounded flex items-center justify-center">
                                  <span className="text-gray-600 text-[8px] font-bold">AMEX</span>
                                </div>
                              </div>

                              {/* Stripe info */}
                              {paymentMethod === 'card' && (
                                <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                  <div className="flex items-start gap-2">
                                    <Info className="h-4 w-4 text-blue-600 mt-0.5" />
                                    <p className="text-sm text-blue-800">
                                      You will be redirected to Stripe's secure checkout page to complete your payment.
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      }
                      
                      return null;
                    })}
                  </div>
                )}
              </div>

              {/* Discount Code */}
              <div className="bg-white rounded-xl p-5">
                <h3 className="font-semibold text-black mb-3">Discount Code</h3>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="text"
                      value={couponCode}
                      onChange={(e) => {
                        setCouponCode(e.target.value.toUpperCase());
                        setCouponApplied(false);
                        setCouponError('');
                      }}
                      placeholder="Enter code"
                      className="h-11 pl-10 text-sm text-black placeholder:text-gray-400 uppercase"
                      disabled={couponApplied}
                      data-testid="coupon-input"
                    />
                  </div>
                  <Button
                    type="button"
                    onClick={handleApplyCoupon}
                    disabled={couponApplied}
                    className={`h-11 px-5 ${
                      couponApplied
                        ? 'bg-green-500 text-white'
                        : 'bg-[#FF8C00] text-white hover:bg-[#E67E00]'
                    }`}
                    data-testid="apply-coupon-btn"
                  >
                    {couponApplied ? (
                      <span className="flex items-center gap-1">
                        <Check className="h-4 w-4" />
                        Applied
                      </span>
                    ) : (
                      'Apply'
                    )}
                  </Button>
                </div>
                {couponError && (
                  <div className="flex items-center gap-1.5 mt-2 text-[#FF8C00]">
                    <AlertCircle className="h-3.5 w-3.5" />
                    <span className="text-xs">{couponError}</span>
                  </div>
                )}
                {couponApplied && (
                  <div className="flex items-center gap-1.5 mt-2 text-green-600">
                    <Check className="h-3.5 w-3.5" />
                    <span className="text-xs">Discount of €{couponDiscount} applied</span>
                  </div>
                )}
              </div>

              {/* Mobile: Legal Checkboxes */}
              <div className="lg:hidden bg-white rounded-xl p-5">
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-3">
                  Required Agreements
                </p>

                <div className="space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreements.terms}
                      onChange={(e) => handleAgreementChange('terms', e.target.checked)}
                      className="mt-1 w-4 h-4 rounded border-gray-300 text-[#FF8C00] focus:ring-[#FF8C00]"
                      data-testid="mobile-checkbox-terms"
                    />
                    <span className="text-sm text-gray-600">
                      I agree to the{' '}
                      <a href="/terms-of-service/" target="_blank" rel="noopener noreferrer" className="text-[#FF8C00] hover:underline">
                        Terms & Conditions
                      </a>
                    </span>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreements.privacy}
                      onChange={(e) => handleAgreementChange('privacy', e.target.checked)}
                      className="mt-1 w-4 h-4 rounded border-gray-300 text-[#FF8C00] focus:ring-[#FF8C00]"
                      data-testid="mobile-checkbox-privacy"
                    />
                    <span className="text-sm text-gray-600">
                      I have read the{' '}
                      <a href="/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-[#FF8C00] hover:underline">
                        Privacy Policy
                      </a>
                    </span>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={agreements.refund}
                      onChange={(e) => handleAgreementChange('refund', e.target.checked)}
                      className="mt-1 w-4 h-4 rounded border-gray-300 text-[#FF8C00] focus:ring-[#FF8C00]"
                      data-testid="mobile-checkbox-refund"
                    />
                    <span className="text-sm text-gray-600">
                      I understand the{' '}
                      <a href="/cancellation-refund-policy/" target="_blank" rel="noopener noreferrer" className="text-[#FF8C00] hover:underline">
                        Refund & Cancellation Policy
                      </a>
                    </span>
                  </label>
                </div>
              </div>

              {/* Info Banner */}
              <div className="bg-white/5 rounded-lg p-4 flex items-start gap-3">
                <Info className="h-5 w-5 text-[#FF8C00] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-300">
                  Your payment is secured with industry-standard encryption. 
                  We never store your full card details.
                </p>
              </div>
            </div>

            {/* Right: Price Summary */}
            <div className="lg:col-span-3 hidden lg:block">
              <div className="sticky top-28">
                <PaymentPriceSummary
                  tripAmount={vehicleDetails?.price || 0}
                  extrasAmount={extrasTotal}
                  discount={couponDiscount}
                  agreements={agreements}
                  onAgreementChange={handleAgreementChange}
                  onBack={handleBack}
                  onPay={handlePay}
                  canPay={canPay}
                  isProcessing={isProcessing}
                  paymentMethod={paymentMethod}
                  autoIncludedExtras={autoIncludedExtras}
                />
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Mobile Payment Bar */}
      <MobilePaymentBar
        total={totalPrice}
        canPay={canPay}
        allAgreed={allAgreed}
        isProcessing={isProcessing}
        onBack={handleBack}
        onPay={handlePay}
        paymentMethod={paymentMethod}
      />

      <Footer />
    </div>
  );
};

export default PaymentPage;
